import {Injectable} from '@angular/core';
import {PremierDernierJoursSemaine} from '../models/premier-dernier-jours-semaine.model';
import {TranslateService} from '@ngx-translate/core';
import {formatDate} from '@angular/common';

@Injectable({
	providedIn: 'root'
})
export class DateUtilsService {

	constructor(private transalteService: TranslateService) {
	}

	formaterDateEnStringPourEnvoiBack(date: Date, locale: string): string {
		return formatDate(date, 'yyyy-MM-dd', locale);
	}

	/**
	 * Renvoie le premier et le dernier jour de la semaine de la date passée en paramètre.
	 * @param date la date.
	 */
	recupererPremierEtDernierJourSemaine(date: Date): PremierDernierJoursSemaine {
		const jour = date.getDay(); // 0 = dimanche, 1 = lundi, ..., 6 = samedi
		const decalageAuLundi = jour === 0 ? 6 : jour - 1; // calcul décalage au lundi
		const premierJour = new Date(date);
		premierJour.setHours(0, 0, 0, 0);
		premierJour.setDate(date.getDate() - decalageAuLundi);

		const dernierJour = new Date(premierJour);
		dernierJour.setDate(premierJour.getDate() + 6);
		dernierJour.setHours(23, 59, 59, 999);

		return new PremierDernierJoursSemaine(premierJour, dernierJour);
	}

	/**
	 * Renvoie le mois traduit qui correspond à l'index passé en paramètre.
	 * @param indexMois l'index du mois {0 = janvier, ..., 11 = décembre}.
	 */
	recupererMoisTraduis(indexMois: number): string {
		switch (indexMois) {
			case 0:
				return this.transalteService.instant('GLOBAL.MOIS.JANVIER');
			case 1:
				return this.transalteService.instant('GLOBAL.MOIS.FEVRIER');
			case 2:
				return this.transalteService.instant('GLOBAL.MOIS.MARS');
			case 3:
				return this.transalteService.instant('GLOBAL.MOIS.AVRIL');
			case 4:
				return this.transalteService.instant('GLOBAL.MOIS.MAI');
			case 5:
				return this.transalteService.instant('GLOBAL.MOIS.JUIN');
			case 6:
				return this.transalteService.instant('GLOBAL.MOIS.JUILLET');
			case 7:
				return this.transalteService.instant('GLOBAL.MOIS.AOUT');
			case 8:
				return this.transalteService.instant('GLOBAL.MOIS.SEPTEMBRE');
			case 9:
				return this.transalteService.instant('GLOBAL.MOIS.OCTOBRE');
			case 10:
				return this.transalteService.instant('GLOBAL.MOIS.NOVEMBRE');
			case 11:
				return this.transalteService.instant('GLOBAL.MOIS.DECEMBRE');
		}
	}

	/**
	 * Renvoie lee jour de la semaine traduit qui correspond à l'index passé en paramètre.
	 * @param indexJour l'index du jour {0 = dimanche, 1 = lundi, ..., 6 = samedi}.
	 */
	recupererJourSemaineTraduit(indexJour: number): string {
		switch (indexJour) {
			case 0:
				return this.transalteService.instant('GLOBAL.JOUR_SEMAINE.DIMANCHE');
			case 1:
				return this.transalteService.instant('GLOBAL.JOUR_SEMAINE.LUNDI');
			case 2:
				return this.transalteService.instant('GLOBAL.JOUR_SEMAINE.MARDI');
			case 3:
				return this.transalteService.instant('GLOBAL.JOUR_SEMAINE.MERCREDI');
			case 4:
				return this.transalteService.instant('GLOBAL.JOUR_SEMAINE.JEUDI');
			case 5:
				return this.transalteService.instant('GLOBAL.JOUR_SEMAINE.VENDREDI');
			case 6:
				return this.transalteService.instant('GLOBAL.JOUR_SEMAINE.SAMEDI');
		}
	}

	/**
	 * Renvoie la date en format "Jour dd mois".
	 * @param date la date à transformer.
	 * @param locale la locale (langue) de l'utilisateur.
	 */
	formaterDateEnFormatLettre(date: Date, locale: string): string {
		const dateFormatee = date.toLocaleDateString(locale, {
			weekday: 'long',
			day: 'numeric',
			month: 'long'
		});

		// On met la première lettre du jour en majuscule.
		return dateFormatee.charAt(0).toUpperCase() + dateFormatee.slice(1);
	}

	/**
	 * Renvoie la date en format "Jour dd mois".
	 * @param date la date à transformer.
	 * @param locale la locale (langue) de l'utilisateur.
	 */
	formaterDateHeureEnFormatLettre(date: Date, locale: string): string {
		const dateFormatee = date.toLocaleDateString(locale, {
			weekday: 'long',
			day: 'numeric',
			month: 'long',
			hour: '2-digit',
			minute: '2-digit'
		});

		// On met la première lettre du jour en majuscule.
		return dateFormatee.charAt(0).toUpperCase() + dateFormatee.slice(1);
	}

	/**
	 * Récupère la date du lundi dans deux semaines.
	 */
	recupererLeLundiDansDeuxSemaines(): Date {
		const aujourdhui = new Date();

		const jourCourant = aujourdhui.getDay();
		// Calcul du nombre de jours jusqu'au prochain lundi
		let nombreDeJoursAvantLeProchainLundi = (8 - jourCourant) % 7;
		if (nombreDeJoursAvantLeProchainLundi === 0) {
			// Si aujourd'hui est lundi, le calcul précédent retourne 0, alors il faut ajouter 7 jours pour aller au lundi suivant
			nombreDeJoursAvantLeProchainLundi = 7;
		}
		// Lundi de la semaine prochaine
		const lundiProchain = new Date(aujourdhui);
		lundiProchain.setDate(aujourdhui.getDate() + nombreDeJoursAvantLeProchainLundi);
		// Ajout de 7 jours pour aller à lundi dans deux semaines
		const lundiDansDeuxSemaines = new Date(lundiProchain);
		lundiDansDeuxSemaines.setDate(lundiProchain.getDate() + 7);
		return lundiDansDeuxSemaines;
	}

	/**
	 * Récupère la date du dimanche dans deux semaines.
	 */
	recupererDimancheDansDeuxSemaines(): Date {
		const lundiDansDeuxSemaines = this.recupererLeLundiDansDeuxSemaines();
		lundiDansDeuxSemaines.setDate(lundiDansDeuxSemaines.getDate() - 1);

		return lundiDansDeuxSemaines;
	}

	/**
	 * Formate la date en chaîne pour la clé de la map d'évènements.
	 *
	 * @param date date à formater.
	 */
	formaterDateEnCle(date: Date): string {
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	}

	/**
	 * Extrait le nombre de minutes depuis minuit d'une date donnée.
	 *
	 * @param {Date} date la date pour laquelle extraire les minutes.
	 * @return {number} le nombre de minutes depuis minuit.
	 */
	toMinutes(date: Date): number {
		return date.getHours() * 60 + date.getMinutes();
	}
}
